import { useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { listingsApi, type ListingAgentDto } from "@/api/buyers";
import { Button, Input, Label } from "@/design-system/components/ui";
import { displayEmail, formatPhone } from "@/design-system/lib/format";
import { listingKeys } from "@/hooks/buyers/buyerKeys";

/**
 * The ONE listing-agent picker: the listing's Edit listing agent modal and the
 * NEW LISTING form both use it, so the two cannot drift.
 *
 * <p>🔴 <b>Searches the SERVER, never a page held in the browser.</b> The selects
 * it replaces loaded the first 200 agents and offered nothing past them, so an
 * agent created later than the 200th could not be picked at all. Each search
 * asks for {@link MAX_RESULTS} matching agents and says so when there are more
 * — "Keep typing to narrow" — instead of quietly showing a partial list.</p>
 *
 * <p>The search is word by word on the server: "Jordan Test" finds Jordan Test,
 * and a word of four or more digits also matches a phone with its formatting
 * stripped, so a phone typed as digits finds the agent.</p>
 *
 * <p>Takes and returns the FULL agent record, not an id: the selected agent's
 * name and firm are shown at all times, including for an agent the current
 * search does not return.</p>
 *
 * <p>Picking an agent changes only what is on screen. The host decides when
 * anything is written — the listing modals write on SAVE and nowhere else.</p>
 */
export interface ListingAgentPickerProps {
  value: ListingAgentDto | null;
  onChange: (agent: ListingAgentDto | null) => void;
  /** Offer "no agent". Clearing shows "— No agent —"; the host decides what
   *  saving that means. */
  allowNone?: boolean;
  /** Renders + NEW AGENT beside the search. The host mounts the New agent form
   *  as a SIBLING modal, so the form never nests inside the host's dialog. */
  onNewAgent?: () => void;
  /** More of the host's own actions, rendered after + NEW AGENT (the Edit
   *  listing agent modal's PASTE TO FILL). */
  extraActions?: ReactNode;
  label?: string;
  disabled?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  testIdPrefix?: string;
  /** Shown instead of "— No agent —" when the host holds an agent id it cannot
   *  show — a listing still pointing at an agent that has since been removed
   *  from the directory. With `allowNone`, the clear control is still offered,
   *  so that agent can be taken off. */
  unresolvedLabel?: string;
}

/** How many agents one search returns. More than this says "Keep typing to
 *  narrow" rather than showing a list that looks complete and is not. */
export const MAX_RESULTS = 20;

/** The shortest term that searches. One character matches most of the book. */
export const MIN_SEARCH_LENGTH = 2;

function useDebounced(value: string, ms = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function ListingAgentPicker({
  value,
  onChange,
  allowNone = false,
  onNewAgent,
  extraActions,
  label = "Listing agent",
  disabled = false,
  inputRef,
  testIdPrefix = "listing-agent-picker",
  unresolvedLabel,
}: ListingAgentPickerProps) {
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const debounced = useDebounced(search);
  const term = debounced.trim();
  const enabled = term.length >= MIN_SEARCH_LENGTH;
  const inputId = `${testIdPrefix}-search`;
  const listboxId = `${testIdPrefix}-results`;

  const results = useQuery({
    queryKey: listingKeys.agentSearch(term),
    queryFn: () => listingsApi.listAgents({ page: 1, pageSize: MAX_RESULTS, q: term }),
    enabled,
  });
  const options = useMemo(() => results.data?.data ?? [], [results.data]);
  const total = results.data?.totalCount ?? options.length;
  const more = total > options.length;
  // The results follow what is in the box NOW, not the debounced term: after a
  // pick clears the box, the old term lingers for the debounce window and the
  // list would otherwise stay open under the agent just chosen.
  const searching = enabled && search.trim().length >= MIN_SEARCH_LENGTH;
  // The listbox exists only while it holds options. "Searching…", a failure and
  // "No agents match" are statuses, not options, so they sit outside it and the
  // combobox reports itself collapsed while they show.
  const listboxShown = searching && options.length > 0;
  // A refetch can shorten the list under a remembered highlight, so the active
  // option is read against the list as it is now.
  const active = activeIndex < options.length ? activeIndex : -1;

  useEffect(() => setActiveIndex(-1), [term]);

  const select = (agent: ListingAgentDto) => {
    onChange(agent);
    setSearch("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && search) {
      // Clears the search without closing the dialog around it — the dialog's
      // own Escape handler only sees the key when there is nothing to clear.
      e.stopPropagation();
      setSearch("");
      return;
    }
    if (!listboxShown) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(active + 1 >= options.length ? 0 : active + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(active <= 0 ? options.length - 1 : active - 1);
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      select(options[active]);
    }
  };

  const setRefs = (el: HTMLInputElement | null) => {
    localInputRef.current = el;
    if (typeof inputRef === "function") inputRef(el);
    else if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
  };

  return (
    <div className="grid gap-space-2" data-testid={testIdPrefix}>
      <Label htmlFor={inputId}>{label}</Label>

      <div
        className="flex flex-wrap items-start justify-between gap-space-2 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
        data-testid={`${testIdPrefix}-selected`}
      >
        {value ? (
          <AgentSummary agent={value} />
        ) : (
          <span className="text-small text-gray-mid">{unresolvedLabel ?? "— No agent —"}</span>
        )}
        {(value || unresolvedLabel) && allowNone && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setTimeout(() => localInputRef.current?.focus(), 0);
            }}
            disabled={disabled}
            aria-label={`Remove ${value?.displayName ?? "the current agent"} — no agent`}
            className="inline-flex items-center gap-space-1 text-caption text-gray-mid hover:text-onyx disabled:opacity-40"
            data-testid={`${testIdPrefix}-clear`}
          >
            <X size={12} aria-hidden="true" /> No agent
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-space-2">
        <div className="relative min-w-[14rem] flex-1">
          <Search
            size={14}
            className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
            aria-hidden="true"
          />
          <Input
            id={inputId}
            ref={setRefs}
            value={search}
            disabled={disabled}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              value
                ? "Search to choose a different agent — name, email, phone or firm"
                : "Search agents by name, email, phone or firm"
            }
            className="pl-[28px]"
            role="combobox"
            aria-expanded={listboxShown}
            aria-controls={listboxShown ? listboxId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={
              listboxShown && active >= 0 ? `${testIdPrefix}-option-${active}` : undefined
            }
            autoComplete="off"
            data-testid={`${testIdPrefix}-input`}
          />
        </div>
        {onNewAgent && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onNewAgent}
            disabled={disabled}
            data-testid={`${testIdPrefix}-new`}
          >
            <Plus size={14} strokeWidth={1.5} aria-hidden /> New agent
          </Button>
        )}
        {extraActions}
      </div>

      {search.trim().length > 0 && search.trim().length < MIN_SEARCH_LENGTH && (
        <p className="text-caption text-gray-mid m-0">Type at least 2 characters to search.</p>
      )}

      {searching && (
        <div>
          {results.isLoading && (
            <p className="m-0 text-small text-gray-mid italic" role="status">
              Searching…
            </p>
          )}
          {results.isError && (
            <p className="m-0 text-small text-status-error" role="alert">
              Search failed. Try again.
            </p>
          )}
          {!results.isLoading && !results.isError && options.length === 0 && (
            <p className="m-0 text-small text-gray-mid" role="status">
              No agents match.{onNewAgent ? " Use New agent to add one." : ""}
            </p>
          )}
          {listboxShown && (
            <ul
              id={listboxId}
              role="listbox"
              aria-label="Matching listing agents"
              className="m-0 p-0 list-none max-h-64 overflow-y-auto rounded-md border border-border-light bg-white-pure"
              data-testid={`${testIdPrefix}-results`}
            >
              {options.map((a, i) => (
                // The input keeps focus (aria-activedescendant), so options are
                // not tab stops; a pointer press must not take focus from it.
                <li
                  key={a.id}
                  id={`${testIdPrefix}-option-${i}`}
                  role="option"
                  aria-selected={value?.id === a.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(a)}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`cursor-pointer px-space-3 py-space-2 text-left ${
                    i === active ? "bg-white-warm" : "bg-white-pure"
                  }`}
                  data-testid={`${testIdPrefix}-option-${a.id}`}
                >
                  <AgentSummary agent={a} />
                </li>
              ))}
            </ul>
          )}
          {listboxShown && more && (
            <p
              className="text-caption text-gray-mid mt-space-1 mb-0"
              data-testid={`${testIdPrefix}-narrow`}
            >
              Showing the first {options.length} of {total} matching agents. Keep typing to
              narrow.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Name and firm on the first line; contact, listing count and the latest
 * listing's name on the second. The second line is what tells two agents with
 * the same name apart.
 */
export function AgentSummary({ agent }: { agent: ListingAgentDto }) {
  const email = displayEmail(agent.email);
  const phone = agent.phone ? formatPhone(agent.phone) : null;
  const detail = [
    email,
    phone,
    agent.listingCount === 0
      ? "No listings"
      : `${agent.listingCount} ${agent.listingCount === 1 ? "listing" : "listings"}`,
    agent.sampleListingName ? `Latest: ${agent.sampleListingName}` : null,
  ].filter(Boolean);

  return (
    <span className="grid min-w-0 gap-[2px]">
      <span className="text-small text-onyx">
        <span className="font-semibold">{agent.displayName}</span>
        {/* A firm contact's label already names the firm ("Firm contact — Acme"). */}
        {agent.firmName && !agent.isFirmContact && (
          <span className="text-gray-mid"> — {agent.firmName}</span>
        )}
      </span>
      {detail.length > 0 && (
        <span className="text-caption text-gray-mid break-words">{detail.join(" · ")}</span>
      )}
    </span>
  );
}
